import { z } from 'zod/v4';

import { StepEnumRuntype } from '../organizationSettings/organizationSettingsBackendTypes';
import { PaymentMethodRuntype, WorkflowTypeRuntype } from '../rfq/rfqBackendTypes';

export const CustomerPortalCalculationTypeRuntype = z.enum(['legacy', 'flexible']);
export type CustomerPortalCalculationType = z.infer<typeof CustomerPortalCalculationTypeRuntype>;

/**
 * Discriminated union mirroring the Rust-side `CustomerPortalCalculationConfig`. The
 * `type` tag is the discriminator: `legacy` carries an optional template; `flexible`
 * carries both a template and the line item whose evaluated value drives the checkout
 * price. Bundling these together prevents the four-field "between" state where one is
 * set without the other.
 */
export const CustomerPortalCalculationConfigRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('legacy'),
        template: z.string().nullable(),
    }),
    z.object({
        type: z.literal('flexible'),
        template: z.string(),
        checkout_price_line_item: z.string(),
    }),
]);
export type CustomerPortalCalculationConfig = z.infer<typeof CustomerPortalCalculationConfigRuntype>;

export const CustomerPortalSettingsRuntype = z.object({
    manufacturing_templates: z.array(z.string()),
    calculation_config: CustomerPortalCalculationConfigRuntype,
    minimum_quotations_lead_time_days: z.number().nullable(),
    shipping_information_text: z.string(),
    shipping_information_link: z.string(),
    payment_information_text: z.string(),
    payment_information_link: z.string(),
    order_confirmation_workflow: WorkflowTypeRuntype,
    invoice_days_before_due: z.number(),
    upload_bom: StepEnumRuntype,
    upload_pcb: StepEnumRuntype,
    upload_cad_files: StepEnumRuntype,
    upload_production_files: StepEnumRuntype,
    upload_pnp_files: StepEnumRuntype,
    cad_files_guidance_text: z.string(),
    manufacturing_files_guidance_text: z.string(),
    imprint_link: z.string(),
    statement_link: z.string(),
    terms_and_conditions_link: z.string(),
    default_sourcing_lead_time_preference: z.number().nullable(),
    show_part_data_warnings: z.boolean(),
    payment_methods: PaymentMethodRuntype,
    use_stripe_for_invoice: z.boolean(),
    custom_conditions_text: z.array(z.string()),
    disable_delivery_date_option: z.boolean(),
    enable_order_confirmation_disclaimer: z.boolean(),
    show_detailed_pcb_price_radar: z.boolean(),
});
export type CustomerPortalSettings = z.infer<typeof CustomerPortalSettingsRuntype>;

export const CustomerPortalSettingsUpdateRuntype = z.object({
    manufacturing_templates: z.array(z.string()).optional(),
    calculation_config: CustomerPortalCalculationConfigRuntype.optional(),
    minimum_quotations_lead_time_days: z.number().nullable().optional(),
    shipping_information_text: z.string().optional(),
    shipping_information_link: z.string().optional(),
    payment_information_text: z.string().optional(),
    payment_information_link: z.string().optional(),
    order_confirmation_workflow: WorkflowTypeRuntype.optional(),
    invoice_days_before_due: z.number().optional(),
    upload_bom: StepEnumRuntype.optional(),
    upload_pcb: StepEnumRuntype.optional(),
    upload_cad_files: StepEnumRuntype.optional(),
    upload_production_files: StepEnumRuntype.optional(),
    upload_pnp_files: StepEnumRuntype.optional(),
    cad_files_guidance_text: z.string().optional(),
    manufacturing_files_guidance_text: z.string().optional(),
    imprint_link: z.string().optional(),
    statement_link: z.string().optional(),
    terms_and_conditions_link: z.string().optional(),
    default_sourcing_lead_time_preference: z.number().nullable().optional(),
    show_part_data_warnings: z.boolean().optional(),
    payment_methods: PaymentMethodRuntype.optional(),
    use_stripe_for_invoice: z.boolean().optional(),
    custom_conditions_text: z.array(z.string()).optional(),
    disable_delivery_date_option: z.boolean().optional(),
    enable_order_confirmation_disclaimer: z.boolean().optional(),
    show_detailed_pcb_price_radar: z.boolean().optional(),
});
export type CustomerPortalSettingsUpdate = z.infer<typeof CustomerPortalSettingsUpdateRuntype>;

export const CustomerPortalPaymentSettingsRuntype = z.object({
    is_payment_processor_setup: z.boolean(),
});
export type CustomerPortalPaymentSettings = z.infer<typeof CustomerPortalPaymentSettingsRuntype>;
